import { useEffect, useState, useCallback, useRef } from 'react'
import { useAdapters } from './useAdapters'
import type { Ride, RideRequest, SearchRideParams, CreateRidePayload } from '../types/ride'

export function useRideSearch() {
  const { rides } = useAdapters()
  const [results, setResults] = useState<Ride[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const unsub = rides.subscribeToRides((live) => setResults(live))
    return unsub
  }, [rides])

  const search = useCallback(async (params: SearchRideParams) => {
    setLoading(true)
    setError(null)
    try {
      const found = await rides.searchRides(params)
      setResults(found)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка поиска')
    } finally {
      setLoading(false)
    }
  }, [rides])

  const requestSeat = useCallback(async (rideId: string, seats = 1) => {
    await rides.requestSeat(rideId, seats)
  }, [rides])

  return { results, loading, error, search, requestSeat }
}

export function useRidePublish() {
  const { rides } = useAdapters()
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [published, setPublished] = useState<Ride | null>(null)

  const publish = useCallback(async (payload: CreateRidePayload): Promise<Ride | null> => {
    setPublishing(true)
    setError(null)
    setPublished(null)
    try {
      const ride = await rides.createRide(payload)
      setPublished(ride)
      return ride
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка публикации')
      return null
    } finally {
      setPublishing(false)
    }
  }, [rides])

  const reset = useCallback(() => { setPublished(null); setError(null) }, [])

  return { publish, publishing, error, published, reset }
}

export function useMyRides() {
  const { rides } = useAdapters()
  const [asDriver, setAsDriver] = useState<Ride[]>([])
  const [asPassenger, setAsPassenger] = useState<RideRequest[]>([])
  const [loading, setLoading] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [d, p] = await Promise.all([rides.getMyRidesAsDriver(), rides.getMyRidesAsPassenger()])
      if (!mountedRef.current) return
      setAsDriver(d)
      setAsPassenger(p)
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [rides])

  useEffect(() => { void load() }, [load])

  const cancelRide = useCallback(async (rideId: string) => {
    await rides.cancelRide(rideId)
    await load()
  }, [rides, load])

  const respondRequest = useCallback(async (requestId: string, status: 'accepted' | 'rejected') => {
    await rides.updateRequest(requestId, status)
    await load()
  }, [rides, load])

  return { asDriver, asPassenger, loading, cancelRide, respondRequest, reload: load }
}
